import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import type { OrgResponse } from "@/lib/types";

interface OrgTabProps {
  org: OrgResponse;
  isAdmin: boolean;
  onUpdated(next: OrgResponse): void;
}

export function OrgTab({ org, isAdmin, onUpdated }: OrgTabProps) {
  const [name, setName] = useState(org.name);
  const [saving, setSaving] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed === org.name || saving) return;
    setSaving(true);
    try {
      const next = await api<OrgResponse>("/orgs/current", {
        method: "PATCH",
        body: JSON.stringify({ name: trimmed }),
      });
      onUpdated(next);
      toast.success("Organization updated");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update organization");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization</CardTitle>
        <CardDescription>The org that owns billing and workspaces.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid max-w-md gap-4" onSubmit={onSubmit}>
          <div className="space-y-1.5">
            <Label htmlFor="org-name">Name</Label>
            <Input
              id="org-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={!isAdmin}
              placeholder="Organization name"
              maxLength={200}
            />
            {!isAdmin ? (
              <p className="text-xs text-muted-foreground">
                Only team admins can rename the organization.
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-plan">Plan</Label>
            <div className="flex items-center gap-2">
              <Badge variant="lemon" id="org-plan">
                {org.plan}
              </Badge>
              <span className="text-xs text-muted-foreground">
                Plan upgrades are handled separately (coming soon).
              </span>
            </div>
          </div>
          {isAdmin ? (
            <div>
              <Button type="submit" disabled={saving || !name.trim() || name.trim() === org.name}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
}
